import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Building2, Check, Clock, Coins, Copy, ShieldCheck, X } from 'lucide-react';
import { ApiClientError, EmptyState, Spinner, relativeTime } from '@riskpay/ui-components';
import { api } from '../lib/client';

/* ==========================================================================
 *  Payout account review
 *
 *  The last human check before a destination can receive money. Approving a
 *  wrong bank account is recoverable with effort; approving a wrong USDT
 *  address is not recoverable at all, which is why the address is shown in
 *  full, in mono, unmasked and unwrapped — the reviewer's job is to read every
 *  character of it against the document the merchant sent.
 * ========================================================================== */

type Rail = 'BANK' | 'CRYPTO';
type Status = 'PENDING_REVIEW' | 'VERIFIED' | 'REJECTED' | 'DISABLED';

interface Account {
  id: string;
  rail: Rail;
  label: string | null;
  status: Status;
  is_primary: boolean;
  review_note: string | null;
  reviewed_at: string | null;
  created_at: string;
  account_holder_name?: string;
  account_number?: string;
  account_last4?: string;
  ifsc_code?: string;
  bank_name?: string;
  branch_name?: string | null;
  account_type?: string;
  asset?: string;
  chain?: string;
  wallet_address?: string;
  wallet_memo?: string | null;
  merchant: {
    id: string;
    ref: string;
    legal_name: string;
    display_name: string;
    kyc_status: string;
    risk_tier: string;
  };
}

const FILTERS = [
  { value: 'PENDING_REVIEW', label: 'Awaiting review' },
  { value: 'VERIFIED', label: 'Verified' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'ALL', label: 'All' },
] as const;

export function PayoutAccounts() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<string>('PENDING_REVIEW');
  const [rail, setRail] = useState<'ALL' | Rail>('ALL');
  const [openId, setOpenId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [makePrimary, setMakePrimary] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const accounts = useQuery({
    queryKey: ['payout-accounts', status, rail],
    queryFn: () => api.get<{ data: Account[] }>(`/settlement-accounts?status=${status}&rail=${rail}`),
    refetchInterval: 30_000,
  });

  const review = useMutation({
    mutationFn: (v: { id: string; decision: 'VERIFIED' | 'REJECTED' }) =>
      api.post(`/settlement-accounts/${v.id}/review`, {
        decision: v.decision,
        note: note.trim() || undefined,
        make_primary: v.decision === 'VERIFIED' && makePrimary,
      }),
    onSuccess: () => {
      setOpenId(null);
      setNote('');
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['payout-accounts'] });
    },
    onError: (err) => setError(err instanceof ApiClientError ? err.message : 'Could not record that decision.'),
  });

  const rows = accounts.data?.data ?? [];
  const pending = rows.filter((a) => a.status === 'PENDING_REVIEW').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Payout accounts</h2>
          <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-content-muted">
            Bank accounts and USDT wallets submitted by merchants. Nothing is settled to a destination until it is
            verified here, whichever rail it is on.
          </p>
        </div>
        {status === 'PENDING_REVIEW' && pending > 0 && (
          <span className="badge badge-warning">
            <Clock className="h-3 w-3" aria-hidden="true" />
            {pending} awaiting review
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-line pb-3">
        <div className="flex rounded-md border border-line bg-surface-inset p-0.5" role="group" aria-label="Status">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setStatus(f.value)}
              aria-pressed={status === f.value}
              className={`rounded px-2.5 py-1 text-2xs font-medium transition-colors ${
                status === f.value ? 'bg-surface-overlay text-content' : 'text-content-subtle hover:text-content-muted'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="flex rounded-md border border-line bg-surface-inset p-0.5" role="group" aria-label="Rail">
          {(['ALL', 'BANK', 'CRYPTO'] as const).map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRail(r)}
              aria-pressed={rail === r}
              className={`rounded px-2.5 py-1 text-2xs font-medium transition-colors ${
                rail === r ? 'bg-surface-overlay text-content' : 'text-content-subtle hover:text-content-muted'
              }`}
            >
              {r === 'ALL' ? 'Both rails' : r === 'BANK' ? 'Bank' : 'USDT'}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <p role="alert" className="flex items-start gap-2 rounded-md border border-danger-ring bg-danger-soft px-3.5 py-3 text-sm text-danger-text">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {error}
        </p>
      )}

      {accounts.isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : accounts.isError ? (
        /* An empty state for a request that failed is a lie: it says "nothing
           to review" when the truth is "we could not ask". */
        <p role="alert" className="flex items-start gap-2 rounded-md border border-danger-ring bg-danger-soft px-3.5 py-3 text-sm text-danger-text">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          Could not load payout accounts.{' '}
          {accounts.error instanceof ApiClientError ? accounts.error.message : 'The request failed.'}
        </p>
      ) : rows.length === 0 ? (
        <div className="panel">
          <EmptyState
            title={status === 'PENDING_REVIEW' ? 'Nothing awaiting review' : 'No accounts match this filter'}
            description="Merchants submit destinations from their portal. They land here the moment they do."
          />
        </div>
      ) : (
        <ul className="space-y-3">
          {rows.map((account) => {
            const open = openId === account.id;
            return (
              <li key={account.id} className="rounded-lg border border-line bg-surface-raised">
                <div className="flex flex-wrap items-start gap-4 p-4 sm:p-5">
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-line bg-surface-inset">
                    {account.rail === 'BANK' ? (
                      <Building2 className="h-4 w-4 text-content-subtle" aria-hidden="true" />
                    ) : (
                      <Coins className="h-4 w-4 text-content-subtle" aria-hidden="true" />
                    )}
                  </span>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-medium text-content">{account.merchant.legal_name}</p>
                      <span className="numeral text-2xs text-content-faint">{account.merchant.ref}</span>
                      <span className={`badge ${account.merchant.kyc_status === 'APPROVED' ? 'badge-success' : 'badge-warning'}`}>
                        kyc {String(account.merchant.kyc_status).toLowerCase()}
                      </span>
                      {account.status !== 'PENDING_REVIEW' && (
                        <span className={`badge ${account.status === 'VERIFIED' ? 'badge-success' : 'badge-danger'}`}>
                          {account.status.toLowerCase().replace('_', ' ')}
                        </span>
                      )}
                      {account.is_primary && <span className="badge badge-neutral">primary</span>}
                    </div>

                    <p className="mt-2 text-2xs uppercase tracking-wider text-content-faint">
                      {account.rail === 'BANK' ? 'Bank account' : `${account.asset} on ${account.chain}`}
                      {account.label ? ` · ${account.label}` : ''} · submitted {relativeTime(account.created_at)}
                    </p>

                    {account.rail === 'BANK' ? (
                      <dl className="mt-2.5 grid gap-x-8 gap-y-1.5 text-xs sm:grid-cols-2">
                        <div className="flex items-center gap-2">
                          <dt className="w-24 shrink-0 text-content-faint">Holder</dt>
                          <dd className="text-content">{account.account_holder_name}</dd>
                        </div>
                        <div className="flex items-center gap-2">
                          <dt className="w-24 shrink-0 text-content-faint">Bank</dt>
                          <dd className="text-content">{account.bank_name}</dd>
                        </div>
                        <div className="flex items-center gap-2">
                          <dt className="w-24 shrink-0 text-content-faint">IFSC</dt>
                          <dd className="numeral text-content flex items-center gap-1.5">
                            <span>{account.ifsc_code}</span>
                            {account.ifsc_code && (
                              <button
                                type="button"
                                title="Copy IFSC"
                                onClick={() => void navigator.clipboard.writeText(account.ifsc_code ?? '')}
                                className="text-content-faint hover:text-content p-0.5 rounded transition-colors"
                              >
                                <Copy className="h-3 w-3" aria-hidden="true" />
                              </button>
                            )}
                          </dd>
                        </div>
                        <div className="flex items-center gap-2">
                          <dt className="w-24 shrink-0 text-content-faint">Account</dt>
                          <dd className="numeral font-medium text-content flex items-center gap-1.5">
                            <span className="tracking-wide">{account.account_number || (account.account_last4 ? `••••${account.account_last4}` : '—')}</span>
                            {(account.account_number || account.account_last4) && (
                              <button
                                type="button"
                                title="Copy account number"
                                onClick={() => void navigator.clipboard.writeText(account.account_number || account.account_last4 || '')}
                                className="text-content-faint hover:text-content p-0.5 rounded transition-colors"
                              >
                                <Copy className="h-3 w-3" aria-hidden="true" />
                              </button>
                            )}
                          </dd>
                        </div>
                        {account.branch_name && (
                          <div className="flex items-center gap-2">
                            <dt className="w-24 shrink-0 text-content-faint">Branch</dt>
                            <dd className="text-content">{account.branch_name}</dd>
                          </div>
                        )}
                        {account.account_type && (
                          <div className="flex items-center gap-2">
                            <dt className="w-24 shrink-0 text-content-faint">Type</dt>
                            <dd className="text-content">{account.account_type}</dd>
                          </div>
                        )}
                      </dl>
                    ) : (
                      <div className="mt-2.5 flex items-start gap-2">
                        <code className="numeral min-w-0 flex-1 break-all rounded border border-line bg-surface-inset px-3 py-2 text-xs leading-relaxed text-content">
                          {account.wallet_address}
                        </code>
                        <button
                          type="button"
                          onClick={() => void navigator.clipboard.writeText(account.wallet_address ?? '')}
                          className="btn btn-secondary btn-sm shrink-0"
                        >
                          <Copy className="h-3 w-3" aria-hidden="true" />
                          Copy
                        </button>
                      </div>
                    )}

                    {account.review_note && (
                      <p className="mt-2.5 text-xs leading-relaxed text-content-subtle">
                        <span className="text-content-faint">Note:</span> {account.review_note}
                      </p>
                    )}
                  </div>

                  {account.status === 'PENDING_REVIEW' && !open && (
                    <button type="button" onClick={() => { setOpenId(account.id); setNote(''); setMakePrimary(true); setError(null); }} className="btn btn-primary btn-sm shrink-0">
                      Review
                    </button>
                  )}
                </div>

                {open && (
                  <div className="border-t border-line bg-surface-inset/40 p-4 sm:p-5">
                    {account.rail === 'CRYPTO' && (
                      <p className="mb-4 flex items-start gap-2 rounded border border-warning-ring bg-warning-soft px-3 py-2.5 text-xs leading-relaxed text-[#FCD34D]">
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        Read the address character by character against the merchant's document. An on-chain transfer to
                        the wrong address cannot be reversed, recalled or disputed.
                      </p>
                    )}

                    <label htmlFor={`note-${account.id}`} className="label">
                      Note to the merchant
                    </label>
                    <textarea
                      id={`note-${account.id}`}
                      rows={2}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Required when rejecting — this text is emailed to them verbatim."
                      className="input h-auto py-2"
                    />

                    <label className="mt-3 flex cursor-pointer items-center gap-2.5 text-xs text-content-muted">
                      <input type="checkbox" checked={makePrimary} onChange={(e) => setMakePrimary(e.target.checked)} />
                      Make this the primary destination for its rail on approval
                    </label>

                    <div className="mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-line pt-4">
                      <button type="button" onClick={() => setOpenId(null)} className="btn btn-ghost btn-sm">
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => review.mutate({ id: account.id, decision: 'REJECTED' })}
                        disabled={review.isPending || note.trim().length < 10}
                        className="btn btn-sm border-danger-ring bg-danger-soft text-danger-text hover:bg-danger-soft disabled:opacity-50"
                      >
                        <X className="h-3.5 w-3.5" aria-hidden="true" />
                        Reject
                      </button>
                      <button
                        type="button"
                        onClick={() => review.mutate({ id: account.id, decision: 'VERIFIED' })}
                        disabled={review.isPending}
                        className="btn btn-primary btn-sm"
                      >
                        {review.isPending ? <Spinner /> : <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />}
                        Verify
                      </button>
                    </div>
                    {note.trim().length > 0 && note.trim().length < 10 && (
                      <p className="mt-2 text-right text-2xs text-content-faint">
                        A rejection needs at least 10 characters of explanation.
                      </p>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <p className="flex items-start gap-2 border-t border-line pt-4 text-2xs leading-relaxed text-content-faint">
        <Check className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
        Every decision here is written to the audit log with your identity, the destination and the note, and emails the
        merchant from the alerts mailbox.
      </p>
    </div>
  );
}
